import './page.scss';

import React, { useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { isUrlValid } from 'utils/validator';
import useResizeObserver from '@react-hook/resize-observer';
import round from 'utils/round';
import sleep from 'utils/sleep';
import { LazyLoadComponent } from 'react-lazy-load-image-component';

import { getAssetUrl, getPageAssetUrl } from 'editor-utils/template';

import PageServerSidePreview from './serverSidePreview';
import PageLayer from '../placeholder/layer';
import { useHash } from 'react-use';

export const PagePropTypes = {
  page: PropTypes.object.isRequired,
  template: PropTypes.object,
  fullWidth: PropTypes.bool,
  className: PropTypes.any,
  onClick: PropTypes.func,
  pageSizeScale: PropTypes.number,
  children: PropTypes.node,
  renderChildrenOnly: PropTypes.bool,
  serverSideDPI: PropTypes.number,
  visibleByDefault: PropTypes.bool,
  withImageMagnify: PropTypes.bool,
};

const COMMON_DPI = 72;

const withPageStyle = Component => {
  const WrappedPage = props => {
    const { template } = props;
    const { visible, ...page } = props.page;
    const pageRef = useRef();

    const [hash] = useHash();
    const isPolotnoOpen = useMemo(() => {
      return hash.includes("polotno")
    }, [hash]);

    const [size, setSize] = useState({});

    const className = classNames({
      'editor-page': true,
      'mx-auto my-auto': true,
      'editor-page-span': page.span,
      // HACK
      'editor-page-no-shadow': ["metail-print-", "acrylic-magnet-"].some(id => template?.id?.indexOf(id) >= 0),
      [props.className]: props.className,
      [page.filter]: page.filter,
    });

    const pageWidth = page.size?.width ?? size.width;
    const pageHeight = page.size?.height ?? size.height;

    let width;
    let height;
    if (props.fullWidth) {
      width = 1;
      height = pageHeight / pageWidth;
    } else if (pageHeight > pageWidth) {
      width = pageWidth / pageHeight;
      height = 1;
    } else {
      width = 1;
      height = pageHeight / pageWidth;
    }

    const pageWidthInPxRef = useRef((page.size?.width * COMMON_DPI) / 2.54);
    const [pageSizeScale, setPageSizeScale] = useState(0);
    const updatePageSizeScale = async (maximumAttempts = 3) => {
      if (props.serverSideDPI) {
        return;
      }

      if (maximumAttempts >= 1) {
        if (pageRef.current) {
          const { width: widthInPx } = pageRef.current.getBoundingClientRect();
          if (widthInPx > 0 && pageWidthInPxRef.current !== widthInPx) {
            pageWidthInPxRef.current = widthInPx;
          }

          const pageWidthInCm = props.page.size.width;
          const pageWidthInPx = pageWidthInPxRef.current;

          setPageSizeScale(2.54 / COMMON_DPI / (pageWidthInCm / pageWidthInPx));
        }
        await sleep(200);
        updatePageSizeScale(maximumAttempts - 1);
      }
    };

    const onLoad = serverSideImage => {
      setSize(serverSideImage);
    };

    useResizeObserver(pageRef, val => {
      if (val?.contentRect?.width != null) {
        const value = round(val.contentRect.width, 0);

        if (value !== pageWidthInPxRef.current) {
          pageWidthInPxRef.current = value;
          updatePageSizeScale();
        }
      }
    });

    const style = {
      width: `${width * 100}%`,
      paddingBottom: `${height * 100}%`,
      cursor: props.onClick ? 'pointer' : 'auto',
    };

    if (props.style?.maxWidth && props.style?.maxHeight) {
      const ratio = width / height;

      if (width / height < props.style.maxWidth / props.style.maxHeight) {
        style.width = props.style.maxHeight * ratio;
        style.height = props.style.maxHeight;
      } else {
        style.width = props.style.maxWidth;
        style.height = props.style.maxWidth / ratio;
      }

      style.paddingBottom = 'unset';
      style.transition = 'none';
    }

    const renderBackground = () => {
      const override = props.page.override ?? {};
      const placeholders = props.page.placeholders ?? [];

      // When page use new approach of background, don't render default one
      if (placeholders.find(p => p.id === 'custom-background')) {
        return null;
      }

      const backgroundImage = override.backgroundImage ?? props.page.backgroundImage;
      if (backgroundImage) {
        if (isUrlValid(backgroundImage)) {
          // TODO work with this type of background as a standard photo placeholder
          return null;
        }

        const src = `${getAssetUrl(template, `/assets_client/images/${backgroundImage}`)}?_`;
        return <PageLayer src={src} className="editor-page-background" />;
      }

      const backgroundColor = override.backgroundColor || props.page.backgroundColor;
      if (backgroundColor) {
        if (backgroundColor.toLowerCase() !== '#ffffff')
          return <div style={{ backgroundColor }} className="editor-page-background" />;
      }

      return (
        <PageLayer
          src={`${getPageAssetUrl(template, props.page.id)}?_`}
          className="editor-page-background"
        />
      );
    };

    return (
      <div ref={pageRef} className={className} style={style} onClick={props.onClick}>
        {props.serverSideDPI ? (
          <>
            {renderBackground()}
            <LazyLoadComponent
              threshold={500}
              visibleByDefault={props.visibleByDefault}
              placeholder={
                <span className="position-absolute w-100 h-100" style={{ left: 0, top: 0 }} />
              }
            >
              <PageServerSidePreview
                page={page}
                template={template}
                photos={props.photos}
                dpi={props.serverSideDPI}
                visible={visible && !isPolotnoOpen}
                withImageMagnify={props.withImageMagnify}
                onLoad={onLoad}
              />
            </LazyLoadComponent>
          </>
        ) : (
          <>
            {props.children}
            {template && !props.renderChildrenOnly && (
              <>
                {renderBackground()}
                <LazyLoadComponent
                  threshold={500}
                  visibleByDefault={props.visibleByDefault}
                  placeholder={
                    <span className="position-absolute w-100 h-100" style={{ left: 0, top: 0 }} />
                  }
                >
                  <Component {...props} pageSizeScale={pageSizeScale} />
                </LazyLoadComponent>
              </>
            )}
          </>
        )}
      </div>
    );
  };

  WrappedPage.propTypes = {
    ...Component.PropTypes,
    ...PagePropTypes,
  };

  WrappedPage.defaultProps = {
    fullWidth: false,
    renderChildrenOnly: false,
    serverSideDPI: 0,
    withImageMagnify: false,
    visibleByDefault: false,
  };

  return WrappedPage;
};

export default withPageStyle;
